// frontend/src/services/apiService.js

import { db } from '../firebase';
import { Timestamp, addDoc, collection, deleteField, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getProjectBlockScreensPath, getProjectStrategiesPath, getProjectStrategyBlocksPath } from '../utils/collectionPaths';
import { getBackendUrl, getCurrentEnvironment } from '../config/backend';

// Function to create strategies with blocks and screens using AI via webhook
export const createStrategyWithBlocksAndScreens = async (userId, projectId, strategyData, name, strategyType, description, sourceData) => {
  // Helper function to remove undefined/null values from objects and arrays
  const removeInvalidValues = (obj) => {
    if (Array.isArray(obj)) {
      // Filter out null/undefined from arrays, then recurse
      return obj
        .filter(item => item !== null && item !== undefined)
        .map(removeInvalidValues);
    }
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }
    
    // For objects, create a new object with valid keys
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && value !== undefined) {
        cleaned[key] = removeInvalidValues(value);
      }
    }
    return cleaned;
  };

  // Helper function to validate and clean data before Firestore
  const validateForFirestore = (data) => {
    const cleaned = removeInvalidValues(data);
    return cleaned;
  };

  // Extract AI-generated fields from the webhook response
  // Handle the new response format from example_strategy_response.json
  let style, tone_of_voice, design_description, strategy_blocks;
  
  // New format: strategyData is an array with strategy_blocks containing output objects
  if (Array.isArray(strategyData) && strategyData.length > 0 && strategyData[0].strategy_blocks) {
    // Extract blocks from the new format where each block has an output property
    strategy_blocks = strategyData[0].strategy_blocks.map(block => block.output).filter(Boolean);
    style = strategyData[0].style || '';
    tone_of_voice = strategyData[0].tone_of_voice || '';
    design_description = strategyData[0].design_description || '';
  } else if (strategyData.output && strategyData.output.strategy_blocks) {
    // Format: strategyData.output.strategy_blocks
    strategy_blocks = strategyData.output.strategy_blocks;
    style = strategyData.output.style || '';
    tone_of_voice = strategyData.output.tone_of_voice || '';
    design_description = strategyData.output.design_description || '';
  } else {
    // Old format: direct fields
    style = strategyData.style || '';
    tone_of_voice = strategyData.tone_of_voice || '';
    design_description = strategyData.design_description || '';
    strategy_blocks = strategyData.strategy_blocks || [];
  }
  
  // Clean the sourceData provided by the user
  const cleanSourceData = validateForFirestore(sourceData || { audiences: [], tasks: [], studyCases: [], reviews: [], ratings: [], bigNumbers: [], product: null }, 'sourceData');
  
  // Prepare strategy document data, prioritizing user input for name, type, and description
  const strategyDataForFirestore = {
    name: name, // from user form
    strategyType: strategyType, // from user form
    description: description || '', // from user form
    style: style || '', // from AI
    toneOfVoice: tone_of_voice || '', // from AI
    designDescription: design_description || '', // from AI
    sourceData: cleanSourceData // from user form
  };

  // Clean the final data object, then add timestamps
  const strategyDoc = {
    ...validateForFirestore(strategyDataForFirestore, 'strategy'),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const strategiesRef = collection(db, getProjectStrategiesPath(projectId));
  const strategyDocRef = await addDoc(strategiesRef, strategyDoc);
  
  // Process and save strategy blocks according to the new schema
  if (strategy_blocks && Array.isArray(strategy_blocks)) {
    for (const block of strategy_blocks) {
      // Prepare block document data (without timestamps)
      const blockDataForFirestore = {
        name: block.name || '',
        blockType: (block.blockType || block.block_type || 'custom').toLowerCase(),
        order: block.order || 0,
        additionalInfo: block.additionalInfo || block['additional info'] || '',
        description: block.description || '',
        dataMapping: Array.isArray(block.dataMapping) ? block.dataMapping : [],
        sourceData: cleanSourceData,
        structuredOutput: block.structuredOutput || block.structured_output ? JSON.stringify(block.structuredOutput || block.structured_output) : '',
        functionMapping: Array.isArray(block.functionMapping) ? block.functionMapping : [],
        taskId: block.taskId || null,
        blockId: block.blockId || null,
      };
      
      // Clean the data, then add timestamps
      const blockDoc = {
        ...validateForFirestore(blockDataForFirestore, `block:${block.name || 'unnamed'}`),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const blocksRef = collection(db, getProjectStrategyBlocksPath(projectId, strategyDocRef.id));
      const blockDocRef = await addDoc(blocksRef, blockDoc);
      
      // Process and save screens for this block according to the new schema
      if (block.screens && block.screens.screens && Array.isArray(block.screens.screens)) {
        for (const screen of block.screens.screens) {
          // Prepare screen document data (without timestamps)
          const screenDataForFirestore = {
            name: screen.name || '',
            type: screen.struct?.screenType || screen.type || '',
            description: screen.description || '',
            screen_id: screen.id || screen.screen_id || '',
            dataMapping: Array.isArray(screen.struct?.dataMapping) ? screen.struct.dataMapping : (Array.isArray(screen.dataMapping) ? screen.dataMapping : []),
            image_prompts: Array.isArray(screen.image_prompts) ? screen.image_prompts : [],
            sourceData: Array.isArray(screen.sourceData) ? screen.sourceData : [],
            struct: screen.struct || null,
          };
          
          // Clean the data, then add timestamps
          const screenDoc = {
            ...validateForFirestore(screenDataForFirestore, `screen:${screen.name || 'unnamed'}`),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };
          
          const screensRef = collection(db, getProjectBlockScreensPath(projectId, strategyDocRef.id, blockDocRef.id));
          const screenDocRef = await addDoc(screensRef, screenDoc);
        }
      }
    }
  }
  
  return {
    strategyId: strategyDocRef.id,
    message: 'Strategy with blocks and screens created successfully'
  };
};

// Firestore-based webhook settings
export const getWebhookSettings = async () => {
  const docRef = doc(db, 'settings', 'webhooks');
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data();
  } else {
    return {};
  }
};

export const updateWebhookSettings = async (settings) => {
  const docRef = doc(db, 'settings', 'webhooks');
  await setDoc(docRef, settings, { merge: true });
};

// Global settings for email notifications
export const getGlobalSettings = async () => {
  const docRef = doc(db, 'settings', 'global');
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    // Backward compatibility: if emailNotifications is a boolean, convert to new structure
    if (typeof data.emailNotifications === 'boolean') {
      return {
        ...data,
        emailNotifications: {
          automations: {
            descriptions: data.emailNotifications,
            audiences: data.emailNotifications,
            actors: data.emailNotifications,
            questions: data.emailNotifications,
            interviews: data.emailNotifications,
            aiActor: data.emailNotifications,
            strategyBlocks: data.emailNotifications,
            aiStrategy: data.emailNotifications,
            structuredOutput: data.emailNotifications,
            imageGeneration: data.emailNotifications
          }
        }
      };
    }
    return data;
  } else {
    return {
      emailNotifications: {
        automations: {
          descriptions: true,
          audiences: true,
          actors: true,
          questions: true,
          interviews: true,
          aiActor: true,
          strategyBlocks: true,
          aiStrategy: true,
          structuredOutput: true,
          imageGeneration: true
        }
      },
      inAppNotifications: true
      // storageBucketId removed - now automatically generated based on project name
    };
  }
};

export const updateGlobalSettings = async (settings) => {
  const docRef = doc(db, 'settings', 'global');
  await setDoc(docRef, settings, { merge: true });
};

// Helper function to check if email notifications are enabled for a specific automation
export const isEmailNotificationEnabled = async (automationType) => {
  try {
    const globalSettings = await getGlobalSettings();
    
    // Check if the specific automation type is enabled
    return globalSettings.emailNotifications?.automations?.[automationType] || false;
  } catch (error) {
    // Default to true if we can't get settings
    return true;
  }
};

export const getEffectiveWebhookUrls = async () => {
  try {
    const settings = await getWebhookSettings();
    
    // Check if all required webhook URLs are configured
    const requiredUrls = [
      'descriptions', 'audiences', 'actors', 'questions', 'interviews', 
      'ai_actor', 'strategyblocks', 'ai_strategy', 'structuredoutput', 'image_generation', 'tasks'
    ];
    
    const missingUrls = requiredUrls.filter(urlKey => !settings[urlKey]);
    
    if (missingUrls.length > 0) {
      throw new Error(`Missing webhook URLs in settings: ${missingUrls.join(', ')}. Please configure all webhook URLs in the Settings page.`);
    }
    
    return {
      descriptions: settings.descriptions,
      audiences: settings.audiences,
      actors: settings.actors,
      questions: settings.questions,
      interviews: settings.interviews,
      ai_actor: settings.ai_actor,
      strategyblocks: settings.strategyblocks,
      ai_strategy: settings.ai_strategy,
      structuredoutput: settings.structuredoutput,
      image_generation: settings.image_generation,
      tasks: settings.tasks
    };
  } catch (e) {
    throw new Error('Failed to load webhook URLs from settings. Please ensure all webhook URLs are configured in the Settings page.');
  }
};


/**
 * Converts @references in plain text to HTML chips with user-friendly labels.
 * @param {string} text
 * @param {Array} audiences
 * @param {Object} actorsByAudience
 * @param {Array} tasks
 * @param {Function} formatReferenceForDisplay
 * @returns {string}
 */
export function referencesToChipsHTML(text, audiences, actorsByAudience, tasks, studyCases, formatReferenceForDisplay) {
  if (!text) return '';
  return text.replace(/(@[\w\[\].,\- ]+)/g, (match) => {
    const label = formatReferenceForDisplay ? formatReferenceForDisplay(match, audiences, actorsByAudience, tasks, studyCases) : match;
    return `<span data-mention-id="${match}" data-label="${label}" class="rendered-mention-chip-html"></span>`;
  });
}

// DEPRECATED: Function to wait for task completion - now handled by TaskContext listeners
// This function is kept for backward compatibility but should not be used
export const waitForTaskCompletion = async (taskId, onProgress = null) => {
  // Return immediately - the TaskContext will handle task completion detection
  if (onProgress) onProgress('Task started. Completion will be detected automatically.');
  
  // Return a promise that never resolves to prevent any polling
  return new Promise(() => {
    // This promise will never resolve - TaskContext handles completion
  });
};

// NEW: Function to create AI strategy through backend proxy (solves timeout and CORS issues)
export const createAIStrategyWithProxy = async (payload, onProgress = null, taskContext = null, userId, projectId) => {
  const backendUrl = getBackendUrl();
  
  // Ensure userId is included in the payload
  if (!payload.userId) {
    payload.userId = userId;
  }
  if (!payload.projectId) {
    payload.projectId = projectId;
  }
  
  // Add environment parameter to payload
  payload.node_env = getCurrentEnvironment();
  
  // Get webhook URLs from Firestore
  const urls = await getEffectiveWebhookUrls();
  
  // Step 1: Start the task through the backend proxy
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.ai_strategy,
      payload: payload,
      action_description: `Creating AI strategy: ${payload.name}`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start AI strategy creation: ${proxyResponse.status} - ${errorText}`);
  }

  const { task_id } = await proxyResponse.json();
  
  if (onProgress) onProgress('AI strategy creation started. This may take several minutes...');

  // Return the task ID immediately so the dialog can be closed
  return { taskId: task_id, status: 'started' };
};

// NEW: Generic proxy function for webhook calls
const callWebhookWithProxy = async (webhookUrl, payload, actionDescription, onProgress = null, taskContext = null, userId = null, projectId = null) => {
  const backendUrl = getBackendUrl();
  
  // Add userId, projectId, and environment to the payload if provided
  const enrichedPayload = {
    ...payload,
    ...(userId && { userId }),
    ...(projectId && { projectId }),
    node_env: getCurrentEnvironment()
  };
  
  // Start the task through the backend proxy
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: webhookUrl,
      payload: enrichedPayload,
      action_description: actionDescription
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start webhook call: ${proxyResponse.status} - ${errorText}`);
  }

  const { task_id } = await proxyResponse.json();
  
  if (onProgress) onProgress(`${actionDescription} started...`);

  // Task completion is now handled by TaskContext listeners
  // Return immediately - no polling needed
  return new Promise(() => {
    // This promise will never resolve - TaskContext handles completion
  });
};

// Proxy-based webhook functions

export const fetchProductDescriptionFromUrlWithProxy = async (productLink, reviewsLink = null, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  const payload = { 
    url: productLink,
    reviewsUrl: reviewsLink,
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };
  
  try {
    // Use the proper backend URL instead of hardcoded relative URL
    const response = await fetch(`${backendUrl}/api/webhook-proxy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        url: urls.descriptions,
        payload: payload,
        action_description: `Fetching product description from URL`
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Return status indicating payload was sent
    return { status: 'payload_sent' };
  } catch (error) {
    throw error;
  }
};

export const fetchAudiencesFromDescriptionWithProxy = async (productDescription, numberOfAudiences, taskData = null, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const requestBody = {
    description: productDescription,
    audiences: Number(numberOfAudiences)
    // userId and projectId will be added by callWebhookWithProxy
  };
  
  const result = await callWebhookWithProxy(
    urls.audiences,
    requestBody,
    `Generating ${numberOfAudiences} audiences from description`,
    onProgress,
    taskContext,
    userId,
    projectId
  );
  
  if (result && Array.isArray(result)) {
    return result;
  } else {
    throw new Error("Audiences webhook returned an unexpected data structure.");
  }
};

export const fetchActorsForBatchAudiencesWithProxy = async (selectedAudiencesArray, numberOfActorsToGenerate, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const requestBody = {
    selected_audiences: selectedAudiencesArray,
    number: Number(numberOfActorsToGenerate)
    // userId and projectId will be added by callWebhookWithProxy
  };
  
  const result = await callWebhookWithProxy(
    urls.actors,
    requestBody,
    `Generating ${numberOfActorsToGenerate} actors for ${selectedAudiencesArray.length} audiences`,
    onProgress,
    taskContext,
    userId,
    projectId
  );
  
  if (result && Array.isArray(result)) {
    return result;
  } else {
    throw new Error(`Actors webhook returned an unexpected data structure for batch creation. Expected a flat array.`);
  }
};


export const fetchProjectInterviewQuestionsWithProxy = async (productDescription, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const requestBody = { 
    description: productDescription
    // userId and projectId will be added by callWebhookWithProxy
  };
  
  const result = await callWebhookWithProxy(
    urls.questions,
    requestBody,
    `Saving the project...`,
    onProgress,
    taskContext,
    userId,
    projectId
  );
  
  // Handle the new response format: [{ output: { questions: "..." } }]
  if (result && Array.isArray(result) && result.length > 0 && result[0].output && result[0].output.questions) {
    return result[0].output.questions;
  }
  
  // Handle the old response format: { interview_questions: [...] }
  if (result && Array.isArray(result.interview_questions)) {
    return result.interview_questions.join('\n\n');
  }
  
  // Handle direct array of strings
  if (Array.isArray(result)) {
    return result.join('\n\n');
  }
  
  // Handle object with questions string
  if (typeof result === 'object' && typeof result.questions === 'string') {
    return result.questions;
  }
  
  // Handle direct string
  if (typeof result === 'string') {
    return result;
  }
  
  return "";
};

export const conductBatchInterviewsWithProxy = async (actorsPayloadWithQuestions, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  
  // Don't add userId and projectId here - they'll be added by callWebhookWithProxy
  const result = await callWebhookWithProxy(
    urls.interviews,
    actorsPayloadWithQuestions,
    `Conducting batch interviews for ${actorsPayloadWithQuestions.length} actors`,
    onProgress,
    taskContext,
    userId,
    projectId
  );
  
  if (result && Array.isArray(result)) {
    return result;
  } else {
    throw new Error("Batch interviews webhook returned an unexpected data structure.");
  }
};

// NEW: Conduct batch interviews with simple payload send (like AI block automation)
export const conductBatchInterviewsWithProxyImmediate = async (actorsPayloadWithQuestions, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  
  // Use the standard callWebhookWithProxy function like other automations
  const result = await callWebhookWithProxy(
    urls.interviews,
    actorsPayloadWithQuestions,
    `Conducting batch interviews for ${actorsPayloadWithQuestions.length} actors`,
    onProgress,
    taskContext,
    userId,
    projectId
  );
  
  // Return status indicating payload was sent
  return { status: 'payload_sent' };
};

export const conductSingleActorInterviewWithProxy = async (actorPayload, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  
  // Don't add userId and projectId here - they'll be added by callWebhookWithProxy
  const result = await callWebhookWithProxy(
    urls.interviews,
    [actorPayload], // Wrap in array to maintain compatibility
    `Conducting interview for actor: ${actorPayload.name}`,
    onProgress,
    taskContext,
    userId,
    projectId
  );
  
  if (result && Array.isArray(result) && result.length > 0) {
    return result[0];
  } else {
    throw new Error("Single actor interview webhook returned an unexpected data structure.");
  }
};

// NEW: Conduct single actor interview with simple payload send (like AI block automation)
export const conductSingleActorInterviewWithProxyImmediate = async (actorPayload, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  
  // Use the standard callWebhookWithProxy function like other automations
  const result = await callWebhookWithProxy(
    urls.interviews,
    [actorPayload], // Wrap in array to maintain compatibility
    `Conducting interview for actor: ${actorPayload.name}`,
    onProgress,
    taskContext,
    userId,
    projectId
  );
  
  // Return status indicating payload was sent
  return { status: 'payload_sent' };
};

export const generateScreensFromStrategyBlockWithProxy = async (blockDetails, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const { name, type, description, usecase, tone, style, screens, blockId, strategyId, firstScreenId } = blockDetails;

  const payload = [{
    name: name || '',
    type: type || '',
    description: description || '',
    use: usecase,
    tone: tone || '',
    style: style || '',
    screens: screens || [],
    blockId: blockId || '',
    strategyId: strategyId || '',
    firstScreenId: firstScreenId || ''
  }];
  
  const backendUrl = getBackendUrl();
  
  // Add userId, projectId, and environment to the payload if provided
  const enrichedPayload = {
    ...payload,
    ...(userId && { userId }),
    ...(projectId && { projectId }),
    node_env: getCurrentEnvironment()
  };
  
  // Start the task through the backend proxy
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.structuredoutput,
      payload: enrichedPayload,
      action_description: `Generating structured output for strategy block: ${name}`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start structured output generation: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress(`Generating structured output for strategy block: ${name} started...`);

  // Return status indicating payload was sent
  return { status: 'payload_sent' };
};

export const createActorWithAIWithProxy = async (actorName, audienceName, audienceDescription, audienceId, processedPrompt, sourceData = null, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  const enrichedPayload = {
    name: actorName,
    audienceName: audienceName,
    audienceDescription: audienceDescription,
    audienceId: audienceId,
    prompt: processedPrompt,
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };

  if (sourceData) {
    enrichedPayload.sourceData = sourceData;
  }
  
  // Send the payload to the backend without expecting a task ID
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.ai_actor,
      payload: enrichedPayload,
      action_description: `Creating AI actor: ${actorName}`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start AI actor creation: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress('AI actor creation started. This may take several minutes...');

  // Don't return a task ID - the TaskContext listener will detect when a new task is created
  return { status: 'payload_sent' };
};

// NEW: Function to create AI actor through backend proxy (returns payload sent confirmation like AI block automation)
export const createActorWithAIWithProxyImmediate = async (actorName, audienceName, audienceDescription, audienceId, processedPrompt, sourceData = null, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  const enrichedPayload = {
    name: actorName,
    audienceName: audienceName,
    audienceDescription: audienceDescription,
    audienceId: audienceId,
    prompt: processedPrompt,
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };

  if (sourceData) {
    enrichedPayload.sourceData = sourceData;
  }
  
  // Send the payload to the backend without expecting a task ID
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.ai_actor,
      payload: enrichedPayload,
      action_description: `Creating AI actor: ${actorName}`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start AI actor creation: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress('AI actor creation started. This may take several minutes...');

  // Don't return a task ID - the TaskContext listener will detect when a new task is created
  return { status: 'payload_sent' };
};

export const generateAudiencesForSingleTaskWithProxy = async (taskPayload, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  // Add userId, projectId, and environment to the payload
  const enrichedPayload = {
    ...taskPayload,
    ...(userId && { userId }),
    ...(projectId && { projectId }),
    node_env: getCurrentEnvironment()
  };
  
  // Send the payload to the backend without expecting a task ID
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.audiences,
      payload: enrichedPayload,
      action_description: `Generating audiences for task`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start audience generation: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress('Audience generation started. This may take several minutes...');

  // Don't return any task ID - the backend will handle all processing and database updates
  return { status: 'payload_sent' };
};

export const createActorsForSingleAudienceWithProxy = async (audiencePayload, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  // Add userId, projectId, and environment to the payload
  const enrichedPayload = {
    ...audiencePayload,
    ...(userId && { userId }),
    ...(projectId && { projectId }),
    node_env: getCurrentEnvironment()
  };
  
  // Send the payload to the backend without expecting a task ID
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.actors,
      payload: enrichedPayload,
      action_description: `Creating actors for audience`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start actors creation: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress('Actors creation started. This may take several minutes...');

  // Don't return any task ID - the backend will handle all processing and database updates
  return { status: 'payload_sent' };
};

// NEW: Proxy versions of parallel functions

export const conductParallelInterviewsWithProxy = async (actorsPayloadArray, onProgressUpdate = null, taskContext = null, userId, projectId) => {
  // actorsPayloadArray is an array of individual actor objects
  const results = [];
  const errors = [];
  
  // Create promises for all interviews
  const interviewPromises = actorsPayloadArray.map(async (actorPayload, index) => {
    try {
      const result = await conductSingleActorInterviewWithProxyImmediate(actorPayload, (message) => {
        if (onProgressUpdate) {
          onProgressUpdate({
            completed: results.length + errors.length,
            total: actorsPayloadArray.length,
            currentActor: actorPayload.name,
            success: true,
            message: message
          });
        }
      }, taskContext, userId, projectId);
      
      // Check if payload was sent successfully
      if (result && result.status === 'payload_sent') {
        results.push({ success: true, index });
        
        // Call progress update callback if provided
        if (onProgressUpdate) {
          onProgressUpdate({
            completed: results.length + errors.length,
            total: actorsPayloadArray.length,
            currentActor: actorPayload.name,
            success: true
          });
        }
        
        return { success: true, index };
      } else {
        throw new Error('Failed to start interview task');
      }
    } catch (error) {
      errors.push({ success: false, error: error.message, actor: actorPayload, index });
      
      // Call progress update callback if provided
      if (onProgressUpdate) {
        onProgressUpdate({
          completed: results.length + errors.length,
          total: actorsPayloadArray.length,
          currentActor: actorPayload.name,
          success: false,
          error: error.message
        });
      }
      
      return { success: false, error: error.message, actor: actorPayload, index };
    }
  });
  
  // Wait for all interviews to complete
  const allResults = await Promise.all(interviewPromises);
  
  return {
    results: allResults.filter(r => r.success).map(r => ({ index: r.index })),
    errors: allResults.filter(r => !r.success),
    totalProcessed: actorsPayloadArray.length,
    successCount: results.length,
    errorCount: errors.length
  };
};

export const generateAudiencesParallelWithProxy = async (tasksPayloadArray, onProgressUpdate = null, taskContext = null, userId, projectId) => {
  // tasksPayloadArray is an array of task payloads
  const results = [];
  const errors = [];
  
  // Create promises for all audience generations
  const audiencePromises = tasksPayloadArray.map(async (taskPayload, index) => {
    try {
      const result = await generateAudiencesForSingleTaskWithProxy(taskPayload, (message) => {
        if (onProgressUpdate) {
          onProgressUpdate({
            completed: results.length + errors.length,
            total: tasksPayloadArray.length,
            currentTask: taskPayload.taskData.name,
            success: true,
            message: message
          });
        }
      }, taskContext, userId, projectId);
      
      // Check if payload was sent successfully
      if (result && result.status === 'payload_sent') {
        results.push({ success: true, taskId: taskPayload.taskData.id, index });
        
        // Call progress update callback if provided
        if (onProgressUpdate) {
          onProgressUpdate({
            completed: results.length + errors.length,
            total: tasksPayloadArray.length,
            currentTask: taskPayload.taskData.name,
            success: true
          });
        }
        
        return { success: true, taskId: taskPayload.taskData.id, index };
      } else {
        throw new Error('Failed to start audience generation task');
      }
    } catch (error) {
      errors.push({ success: false, error: error.message, task: taskPayload, index });
      
      // Call progress update callback if provided
      if (onProgressUpdate) {
        onProgressUpdate({
          completed: results.length + errors.length,
          total: tasksPayloadArray.length,
          currentTask: taskPayload.taskData.name,
          success: false,
          error: error.message
        });
      }
      
      return { success: false, error: error.message, task: taskPayload, index };
    }
  });
  
  // Wait for all audience generations to complete
  const allResults = await Promise.all(audiencePromises);
  
  return {
    results: allResults.filter(r => r.success),
    errors: allResults.filter(r => !r.success),
    totalProcessed: tasksPayloadArray.length,
    successCount: results.length,
    errorCount: errors.length
  };
};

export const createActorsParallelWithProxy = async (audiencesPayloadArray, onProgressUpdate = null, taskContext = null, userId, projectId) => {
  // audiencesPayloadArray is an array of audience payloads
  const results = [];
  const errors = [];
  
  // Create promises for all actor creations
  const actorPromises = audiencesPayloadArray.map(async (audiencePayload, index) => {
    try {
      const result = await createActorsForSingleAudienceWithProxy(audiencePayload, (message) => {
        if (onProgressUpdate) {
          onProgressUpdate({
            completed: results.length + errors.length,
            total: audiencesPayloadArray.length,
            currentAudience: audiencePayload.selected_audiences[0].name,
            success: true,
            message: message
          });
        }
      }, taskContext, userId, projectId);
      
      // Check if payload was sent successfully
      if (result && result.status === 'payload_sent') {
        const audienceName = audiencePayload.selected_audiences[0].name;
        results.push({ success: true, audienceName, index });
        
        // Call progress update callback if provided
        if (onProgressUpdate) {
          onProgressUpdate({
            completed: results.length + errors.length,
            total: audiencesPayloadArray.length,
            currentAudience: audienceName,
            success: true
          });
        }
        
        return { success: true, audienceName, index };
      } else {
        throw new Error('Failed to start actors creation task');
      }
    } catch (error) {
      errors.push({ success: false, error: error.message, audience: audiencePayload, index });
      
      // Call progress update callback if provided
      if (onProgressUpdate) {
        onProgressUpdate({
          completed: results.length + errors.length,
          total: audiencesPayloadArray.length,
          currentAudience: audiencePayload.selected_audiences[0].name,
          success: false,
          error: error.message
        });
      }
      
      return { success: false, error: error.message, audience: audiencePayload, index };
    }
  });
  
  // Wait for all actor creations to complete
  const allResults = await Promise.all(actorPromises);
  
  return {
    results: allResults.filter(r => r.success),
    errors: allResults.filter(r => !r.success),
    totalProcessed: audiencesPayloadArray.length,
    successCount: results.length,
    errorCount: errors.length
  };
};

export const createBlockWithAIWithProxy = async (blockPayload, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  const enrichedPayload = {
    ...blockPayload,
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };
  
  // Step 1: Start the task through the backend proxy (immediate return like strategy creation)
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.strategyblocks,
      payload: enrichedPayload,
      action_description: `Creating AI strategy block: ${blockPayload.name || 'unnamed'}`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start AI block creation: ${proxyResponse.status} - ${errorText}`);
  }

  const { task_id } = await proxyResponse.json();
  
  if (onProgress) onProgress('AI block creation started. This may take several minutes...');

  // Return the task ID immediately so the dialog can be closed (like strategy creation)
  return { taskId: task_id, status: 'started' };
};

// New function for update block with AI - only sends payload, no task ID creation
export const updateBlockWithAISimple = async (blockPayload, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  const enrichedPayload = {
    ...blockPayload,
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };
  
  // Send the payload to the backend without expecting a task ID
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.strategyblocks,
      payload: enrichedPayload,
      action_description: `Updating AI strategy block: ${blockPayload.name || 'unnamed'}`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start AI block update: ${proxyResponse.status} - ${errorText}`);
  }

  // Don't return a task ID - the TaskContext listener will detect when a new task is created
  return { status: 'payload_sent' };
  
};

/**
 * Invite a user to a project by creating an invitation and email document.
 * Uses upsert functionality to update existing invitations instead of creating duplicates.
 * @param {Object} params
 * @param {string} params.inviterId - The inviter's UID
 * @param {string} params.recipientEmail - The invitee's email
 * @param {string} params.projectId - The project ID
 * @param {string} params.roleToAssign - The role to assign ("member" or "owner")
 * @returns {Promise<{invitationId: string, inviteLink: string}>}
 */
export const inviteUserToProject = async ({ inviterId, recipientEmail, projectId, roleToAssign }) => {
  const createdAt = Timestamp.now();
  const expiresAt = Timestamp.fromMillis(createdAt.toMillis() + 7 * 24 * 60 * 60 * 1000); // 7 days later

  // Generate a 64-character hex token (256 bits) using Web Crypto API
  const generateSecureToken = () => {
    // Use Web Crypto API for cryptographically secure random values
    const array = new Uint8Array(32); // 32 bytes = 256 bits
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const token = generateSecureToken();
  const baseUrl = window.location.origin;
  const inviteLink = `${baseUrl}/invite?token=${token}`;

  // Create a unique identifier for the invitation based on project and recipient
  const invitationId = `${projectId}_${recipientEmail.replace(/[^a-zA-Z0-9]/g, '_')}`;

  // Fetch inviter's email and project name for the email template
  let inviterEmail = '';
  let projectName = '';
  
  try {
    // Get inviter's email
    const inviterRef = doc(db, 'users', inviterId);
    const inviterSnap = await getDoc(inviterRef);
    if (inviterSnap.exists()) {
      inviterEmail = inviterSnap.data().email || '';
    }

    // Get project name
    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);
    if (projectSnap.exists()) {
      projectName = projectSnap.data().projectName || '';
    }
  } catch (error) {
    // Continue with empty values if fetch fails
  }

  // 1. Upsert invitation document using setDoc with merge
  const invitationRef = doc(db, 'invitations', invitationId);
  await setDoc(invitationRef, {
    inviterId,
    recipientEmail,
    projectId,
    roleToAssign,
    status: 'pending',
    token,
    createdAt,
    expiresAt,
    updatedAt: createdAt,
    acceptedAt: deleteField(),
    acceptedBy: deleteField()
  }, { merge: true });

  // 2. Create email document
  await addDoc(collection(db, 'mail'), {
    to: [recipientEmail],
    template: {
      name: 'invite',
      data: {
        inviteLink,
        inviterEmail,
        projectName
      }
    }
  });

  return { invitationId, inviteLink };
}; 

export const generateQuestionsWithProxyImmediate = async (productDescription, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  // Ensure userId, projectId, and environment are included in the payload
  const enrichedPayload = {
    description: productDescription,
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };
  
  // Send the payload to the backend without expecting a task ID
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.questions,
      payload: enrichedPayload,
      action_description: `Generating questions for project`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start questions generation: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress('Questions generation started. This may take several minutes...');

  // Don't return a task ID - the TaskContext listener will detect when a new task is created
  return { status: 'payload_sent' };
};

export const generateQuestionsWithAllProductFields = async (productData, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  // Ensure userId, projectId, and environment are included in the payload along with all product fields
  const enrichedPayload = {
    ...productData, // Include all product fields
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };
  
  // Send the payload to the backend without expecting a task ID
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.questions,
      payload: enrichedPayload,
      action_description: `Generating questions for project with all product fields`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start questions generation: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress('Questions generation started with all product fields. This may take several minutes...');

  // Don't return a task ID - the TaskContext listener will detect when a new task is created
  return { status: 'payload_sent' };
};

export const generateTasksFromDescription = async (productData, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  // Ensure userId, projectId, and environment are included in the payload along with all product fields
  const enrichedPayload = {
    ...productData, // Include all product fields
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };
  
  // Send the payload to the backend without expecting a task ID
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.tasks, // Use tasks webhook URL
      payload: enrichedPayload,
      action_description: `Generating tasks from product description with all product fields`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start tasks generation: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress('Tasks generation started with all product fields. This may take several minutes...');

  // Don't return a task ID - the TaskContext listener will detect when a new task is created
  return { status: 'payload_sent' };
}; 

// NEW: Fetch reviews using backend proxy. Prefers dedicated 'reviews' webhook if configured; falls back to 'descriptions'.
export const fetchReviewsWithProxyImmediate = async (reviewsUrl, tasksArray = [], onProgress = null, taskContext = null, userId, projectId) => {
  const settings = await getWebhookSettings();
  const backendUrl = getBackendUrl();

  const targetUrl = settings.reviews || settings.descriptions;
  if (!targetUrl) {
    throw new Error('Missing webhook URL. Please configure either a Reviews or Descriptions webhook URL in Settings.');
  }

  const enrichedPayload = {
    reviewsUrl: reviewsUrl,
    tasks: Array.isArray(tasksArray) ? tasksArray : [],
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };

  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: targetUrl,
      payload: enrichedPayload,
      action_description: 'Fetching reviews from URL'
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start reviews fetch: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress('Reviews fetch started. This may take several minutes...');

  return { status: 'payload_sent' };
};

export const generateImageWithProxy = async (payload, onProgress = null, taskContext = null, userId, projectId) => {
  const urls = await getEffectiveWebhookUrls();
  const backendUrl = getBackendUrl();
  
  // Add userId, projectId, and environment to the payload
  const enrichedPayload = {
    ...payload,
    userId: userId,
    projectId: projectId,
    node_env: getCurrentEnvironment()
  };
  
  // Start the task through the backend proxy
  const proxyResponse = await fetch(`${backendUrl}/api/webhook-proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: urls.image_generation,
      payload: enrichedPayload,
      action_description: `Generating image for media component`
    })
  });

  if (!proxyResponse.ok) {
    const errorText = await proxyResponse.text();
    throw new Error(`Failed to start image generation: ${proxyResponse.status} - ${errorText}`);
  }

  if (onProgress) onProgress('Image generation started. This may take several minutes...');

  // Return status indicating payload was sent
  return { status: 'payload_sent' };
};

// Generate comprehensive mock strategy data (development only)
export const generateMockStrategyData = async () => {
  const backendUrl = getBackendUrl();
  
  // Check if we're in development mode
  if (process.env.NODE_ENV !== 'development') {
    throw new Error('Mock data generation is only available in development environment');
  }
  
  try {
    const response = await fetch(`${backendUrl}/api/generate-mock-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to generate mock data: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
    
  } catch (error) {
    throw error;
  }
};
